import { useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StressTestSliderProps {
  value: number; // 0 to -50
  onChange: (value: number) => void;
  disabled?: boolean;
  compact?: boolean;
}

const PRESETS = [
  { label: 'None', shortLabel: 'None', value: 0, description: 'No stress scenario' },
  { label: 'Mild Correction', shortLabel: 'Mild', value: -15, description: 'Typical market pullback' },
  { label: '2020 COVID', shortLabel: 'COVID', value: -34, description: 'March 2020 crash' },
  { label: '2008 Crisis', shortLabel: '2008', value: -50, description: 'Financial crisis severity' },
];

export function StressTestSlider({ value, onChange, disabled = false, compact = false }: StressTestSliderProps) {
  const [isDragging, setIsDragging] = useState(false);

  const getStressColor = (val: number) => {
    if (val >= -10) return 'text-status-good';
    if (val >= -25) return 'text-status-warning';
    return 'text-status-critical';
  };

  const getStressBgColor = (val: number) => {
    if (val >= -10) return 'bg-status-good/10';
    if (val >= -25) return 'bg-status-warning/10';
    return 'bg-status-critical/10';
  };

  const activePreset = PRESETS.find(p => p.value === value);

  // Compact mode
  if (compact) {
    return (
      <div className={cn('space-y-3', disabled && 'opacity-50 pointer-events-none')}>
        {/* Current value badge */}
        <div className="flex items-center justify-between">
          <Badge
            variant="outline"
            className={cn(
              'font-mono text-xs transition-colors',
              getStressColor(value),
              getStressBgColor(value)
            )}
          >
            {value === 0 ? 'None' : `${value}%`}
          </Badge>
        </div>

        {/* Slider */}
        <Slider
          value={[Math.abs(value)]}
          onValueChange={([v]) => onChange(-v)}
          onPointerDown={() => setIsDragging(true)}
          onPointerUp={() => setIsDragging(false)}
          max={50}
          min={0}
          step={1}
          className="w-full"
        />

        {/* Compact preset buttons */}
        <div className="grid grid-cols-4 gap-1">
          {PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant={value === preset.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChange(preset.value)}
              className={cn(
                'text-[10px] h-7 px-1',
                value === preset.value && preset.value < -25 && 'bg-status-critical hover:bg-status-critical/90'
              )}
            >
              {preset.shortLabel}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  // Full mode
  return (
    <div className={cn('space-y-4', disabled && 'opacity-50 pointer-events-none')}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Stress Test Scenario</span>
        </div>
        <Badge
          variant="outline"
          className={cn(
            'font-mono transition-colors',
            getStressColor(value),
            getStressBgColor(value)
          )}
        >
          {value === 0 ? 'None' : `${value}%`}
        </Badge>
      </div>

      {/* Slider */}
      <div className="relative pt-2 pb-1">
        <Slider
          value={[Math.abs(value)]}
          onValueChange={([v]) => onChange(-v)}
          onPointerDown={() => setIsDragging(true)}
          onPointerUp={() => setIsDragging(false)}
          max={50}
          min={0}
          step={1}
          className="w-full"
        />

        {/* Tick marks */}
        <div className="flex justify-between mt-1 px-0.5">
          <span className="text-[10px] text-muted-foreground">0%</span>
          <span className="text-[10px] text-muted-foreground">-25%</span>
          <span className="text-[10px] text-muted-foreground">-50%</span>
        </div>
      </div>

      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <Button
            key={preset.value}
            variant={value === preset.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(preset.value)}
            className={cn(
              'text-xs h-8',
              value === preset.value && preset.value < -25 && 'bg-status-critical hover:bg-status-critical/90'
            )}
          >
            {preset.value < 0 && <AlertTriangle className="h-3 w-3 mr-1" />}
            {preset.label}
          </Button>
        ))}
      </div>

      {/* Active preset description */}
      {activePreset && value !== 0 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <AlertTriangle className={cn('h-3.5 w-3.5', getStressColor(value))} />
          {activePreset.description}
          {value <= -34 && (
            <span className="text-status-critical font-medium ml-1">
              — Severe market downturn simulation
            </span>
          )}
        </p>
      )}

      {/* Educational note */}
      {isDragging && (
        <p className="text-xs text-muted-foreground italic animate-fade-in">
          Drag to simulate different market stress levels. Tactical funds typically preserve more capital during downturns.
        </p>
      )}
    </div>
  );
}
